import React, { useCallback, useEffect, useState } from "react";
import { Button, Switch, message } from "antd";

import * as api from "./controller";
import "./leadSettings.css";

/**
 * CRM Settings → Registration onboarding: which optional steps new registrants see.
 *
 * Each registrant keeps the switches as they were when they signed up, so a change
 * here only affects people who register afterwards — nobody is re-routed mid-way.
 * The copy for each step comes from the backend (clinics.services.onboarding), the
 * single place that also decides the screen order.
 */
export default function OnboardingStepsPanel() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(null); // key being saved

  const load = useCallback(async () => {
    const r = await api.getOnboardingSettings();
    const ok = r.status >= 200 && r.status < 300 && r.json && Array.isArray(r.json.steps);
    setFailed(!ok);
    setData(ok ? r.json : null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, enabled) => {
    const before = data;
    // Show the change at once; put it back if the save fails.
    setData({ ...data, steps: data.steps.map((s) => (s.key === key ? { ...s, enabled } : s)) });
    setSaving(key);
    const r = await api.updateOnboardingSettings({ [key]: enabled });
    setSaving(null);
    if (r.status >= 200 && r.status < 300 && r.json && Array.isArray(r.json.steps)) {
      setData(r.json);
    } else {
      setData(before);
      message.error((r.json && r.json.detail) || "Could not save the onboarding steps.");
    }
  };

  if (failed) {
    return (
      <div className="leadset-panel">
        <p className="leadset-panel-title">Onboarding steps</p>
        <p className="crm-sub leadset-panel-sub">
          Couldn&rsquo;t load the onboarding steps.{" "}
          <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
        </p>
      </div>
    );
  }

  return (
    <div className="leadset-panel">
      <p className="leadset-panel-title">Onboarding steps</p>
      <p className="crm-sub leadset-panel-sub">
        Choose which optional steps people see when they register. Changes apply to new
        registrations only; anyone already signed up keeps the steps that were on when they
        registered. Steps that were off show as N/A in their readiness and never trigger
        reminders.
        {data && data.always_shown && data.always_shown.length
          ? ` ${data.always_shown.join(" and ")} are always shown.`
          : ""}
      </p>
      {(data ? data.steps : []).map((step) => (
        <div className="leadset-row" key={step.key}>
          <div className="leadset-row-label">
            {step.label}
            <div className="leadset-hint">{step.description}</div>
          </div>
          <div className="leadset-row-control">
            <Switch
              checked={!!step.enabled}
              loading={saving === step.key}
              onChange={(v) => toggle(step.key, v)}
              aria-label={`${step.label}: ${step.enabled ? "shown" : "hidden"} at registration`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
